import Grid from "@mui/material/Grid";
import { SxProps, Theme } from "@mui/system";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormControlNumberInput } from "@/components/formControls/formControlNumberInput";
import { FormControlToggleButtonGroup, ToggleOption } from "@/components/formControls/formControlToggleButtonGroup";
import { RentReferenceEnum } from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

import {
  StyledFormGridContainer,
  StyledFormSection,
  StyledFormSectionHeader,
  StyledFormSectionTitle,
} from "../refineRealEstate.styled";

const toggleButtonGroupStyle: SxProps<Theme> = {
  gap: "4px",
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  height: "55px",
  "& .MuiToggleButton-root": {
    fontSize: "12px",
  },
};

interface FinancialAndRentalInfoTabProps {
  readonly?: boolean;
}

export const FinancialAndRentalInfoTab = ({ readonly }: FinancialAndRentalInfoTabProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });

  const { control, watch, setValue } = useFormContext<PropertyInformation>();

  const { buildingData, financialAndRentalInfo } = watch();

  useEffect(() => {
    if (financialAndRentalInfo.rent?.reference == null) {
      setValue("financialAndRentalInfo.rent.reference", RentReferenceEnum.euroPerSquareMeter, {
        shouldDirty: false,
      });
    }
  }, [financialAndRentalInfo.rent?.reference, setValue]);

  useEffect(() => {
    if (!buildingData.garages || buildingData.garages === 0)
      setValue("financialAndRentalInfo.garageRent.value", undefined);
    if (!buildingData.outdoorParkingSpaces || buildingData.outdoorParkingSpaces === 0)
      setValue("financialAndRentalInfo.parkingSpaceRent.value", undefined);
  }, [buildingData, setValue]);

  //const isGaragesActive = !!buildingData?.garages && buildingData?.garages > 0;
  //const isParkingSpacesActive = !!buildingData?.outdoorParkingSpaces && buildingData?.outdoorParkingSpaces > 0;

  const rentReferenceOptions: ToggleOption<RentReferenceEnum>[] = useMemo(
    () => [
      { value: RentReferenceEnum.euroPerSquareMeter, label: t("euroSqm"), disabled: !!readonly },
      { value: RentReferenceEnum.euroPerYear, label: t("euroYear"), disabled: !!readonly },
    ],
    [t, readonly],
  );

  return (
    <StyledFormGridContainer>
      <StyledFormSectionHeader>{t("dataVerification.financialAndRentalInfo")}</StyledFormSectionHeader>
      <StyledFormSection>
        <StyledFormSectionTitle>{t("rents")}</StyledFormSectionTitle>
        <Grid container spacing={"10px"} columns={12}>
          <Grid size={8}>
            <FormControlNumberInput name="financialAndRentalInfo.rent.value" control={control} sx={{ width: "100%" }} />
          </Grid>
          <Grid size={4}>
            <FormControlToggleButtonGroup
              control={control}
              name="financialAndRentalInfo.rent.reference"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={rentReferenceOptions}
            />
          </Grid>
        </Grid>
      </StyledFormSection>

      {/* <StyledFormSection sx={{ opacity: isGaragesActive ? 1 : 0.5 }}>
        <StyledFormSectionTitle>{t("garageRent")}</StyledFormSectionTitle>
        <Grid container spacing={1.25} columns={12}>
          <Grid size={8}>
            <FormControlNumberInput
              name="financialAndRentalInfo.garageRent.value"
              control={control}
              sx={{ width: "100%" }}
              disabled={!isGaragesActive || !!readonly}
            />
          </Grid>
          <Grid size={4}>
            <FormControlToggleButtonGroup
              control={control}
              name={"financialAndRentalInfo.garageRent.reference"}
              fullWidth
              sx={toggleButtonGroupStyle}
              options={
                isGaragesActive || !!readonly
                  ? rentReferenceOptions
                  : rentReferenceOptions.map((option) => ({ ...option, disabled: true }))
              }
            />
          </Grid>
        </Grid>
      </StyledFormSection> */}

      {/* <StyledFormSection sx={{ opacity: isParkingSpacesActive ? 1 : 0.5 }}>
        <StyledFormSectionTitle>{t("parkingSpaceRent")}</StyledFormSectionTitle>
        <Grid container spacing={"10px"} columns={12}>
          <Grid size={8}>
            <FormControlNumberInput
              name="financialAndRentalInfo.parkingSpaceRent.value"
              control={control}
              sx={{ width: "100%" }}
              disabled={!isParkingSpacesActive || !!readonly}
            />
          </Grid>
          <Grid size={4}>
            <FormControlToggleButtonGroup
              control={control}
              name="financialAndRentalInfo.parkingSpaceRent.reference"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={
                isParkingSpacesActive || !!readonly
                  ? rentReferenceOptions
                  : rentReferenceOptions.map((option) => ({ ...option, disabled: true }))
              }
            />
          </Grid>
        </Grid>
      </StyledFormSection> */}
    </StyledFormGridContainer>
  );
};
